import { useEffect, useState } from 'react'
import { experienciasApi } from '../api/experiencias'
import Boton from './Boton'
import Modal from './Modal'
import SelectorArticulosModal from './SelectorArticulosModal'
import { colors, radius, space, sx, text } from '../design/tokens'

/**
 * MENÚ IMPRESO de una experiencia (Martin, 10 oct 2026): "un botón generar
 * menú… traés los datos del evento para generar el menú en PDF que se imprime
 * en la cena; un lugar para incluir los vinos y el menú; con los vinos un QR
 * que agrega todos al carrito; los dos formatos y que se elija".
 *
 * La primera vez llega PROPUESTO desde el evento (título, fecha, «Los vinos»,
 * «El menú»); lo que se corrige acá queda guardado para la próxima. Los vinos
 * se eligen con el selector único de artículos: los vinculados traen marca,
 * nombre y añada del maestro y son los que suma el QR.
 */

const vacioPaso = { plato: '', detalle: '' }

const ETIQUETAS = {
  etiqueta: 'Arriba del título', titulo: 'Título', bajada: 'Bajada', fecha: 'Fecha', pie: 'Pie',
  sommelier: 'Sommelier', chef: 'Chef', texto_qr: 'Contratapa',
}

/** "pasos.1.detalle" → "Paso 2 · detalle"; "vinos.3.nombre" → "Vino 04". */
function nombreCampo(campo) {
  const [lista, i, sub] = campo.split('.')
  if (lista === 'vinos') return `Vino ${String(Number(i) + 1).padStart(2, '0')}`
  if (lista === 'pasos') return `Paso ${Number(i) + 1} · ${sub}`
  return ETIQUETAS[campo] || campo
}

/** Reemplaza la palabra entera (no adentro de otra) en el texto. */
function reemplazar(texto, palabra, por) {
  const esc = palabra.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return String(texto).replace(new RegExp(`(?<![\\p{L}])${esc}(?![\\p{L}])`, 'gu'), por)
}

/** Esquema del formato: dos paneles por cara, como sale la hoja. */
function Esquema({ vertical }) {
  const w = vertical ? 30 : 42
  const h = vertical ? 42 : 30
  const panel = { position: 'absolute', top: 3, bottom: 3, width: (w - 9) / 2, border: `1px solid ${colors.textMeta}` }
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: w, height: h, border: `1px solid ${colors.border}`, background: colors.bg, flexShrink: 0 }}>
      <span style={{ ...panel, left: 3 }} />
      <span style={{ ...panel, right: 3 }} />
    </span>
  )
}

function Campo({ label, children, ancho }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: space.xs, flex: ancho ? `0 0 ${ancho}px` : 1, minWidth: 0 }}>
      <span style={estilos.label}>{label}</span>
      {children}
    </label>
  )
}

/** ↑ ↓ ✕ de un renglón. */
function Mover({ i, total, onMover, onQuitar }) {
  return (
    <span style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
      <Boton variante="ghost" sm disabled={i === 0} onClick={() => onMover(i, -1)} title="Subir">↑</Boton>
      <Boton variante="ghost" sm disabled={i === total - 1} onClick={() => onMover(i, 1)} title="Bajar">↓</Boton>
      <Boton variante="ghost" sm onClick={() => onQuitar(i)} title="Quitar">✕</Boton>
    </span>
  )
}

export default function MenuExperienciaModal({ experiencia, onClose }) {
  const [menu, setMenu] = useState(null)
  const [formatos, setFormatos] = useState({})
  const [urlQr, setUrlQr] = useState(null)
  const [guardado, setGuardado] = useState(false)
  const [error, setError] = useState('')
  const [aviso, setAviso] = useState('')
  const [trabajando, setTrabajando] = useState('')
  // null = cerrado; 'agregar' = varios al final; número = vincular ese renglón.
  const [selector, setSelector] = useState(null)
  // Corrector: null = sin revisar; [] = sin faltas; lista = hallazgos pendientes.
  const [hallazgos, setHallazgos] = useState(null)
  const [corrector, setCorrector] = useState(true)
  const [antesDeImprimir, setAntesDeImprimir] = useState(false)

  useEffect(() => {
    experienciasApi.menu(experiencia.id)
      .then(({ data }) => {
        setMenu(data.menu)
        setFormatos(data.formatos)
        setUrlQr(data.url_qr)
        setGuardado(data.guardado)
      })
      .catch(() => setError('No se pudo cargar el menú de esta experiencia.'))
  }, [experiencia.id])

  const set = (campo, valor) => { setMenu((m) => ({ ...m, [campo]: valor })); setAviso('') }
  const setFila = (lista, i, campo, valor) => set(lista, menu[lista].map((f, j) => (j === i ? { ...f, [campo]: valor } : f)))
  const mover = (lista) => (i, d) => {
    const filas = [...menu[lista]]
    ;[filas[i], filas[i + d]] = [filas[i + d], filas[i]]
    set(lista, filas)
  }
  const quitar = (lista) => (i) => set(lista, menu[lista].filter((_, j) => j !== i))

  const vinculados = menu ? menu.vinos.filter((v) => v.codigo).length : 0
  const sinArticulo = menu ? menu.vinos.length - vinculados : 0

  // Los artículos elegidos vuelven como renglones armados por el backend
  // (marca, nombre limpio y añada salen del maestro — una sola regla).
  const alElegir = async (articulos) => {
    const ids = articulos.map((a) => a.id)
    const destino = selector
    setSelector(null)
    if (!ids.length) return
    try {
      const { data } = await experienciasApi.menuVinos(ids)
      if (typeof destino === 'number') {
        set('vinos', menu.vinos.map((v, j) => (j === destino ? data.vinos[0] : v)))
      } else {
        // Un vino cargado como texto (sin artículo) se reemplaza por el vinculado en vez de duplicarse.
        set('vinos', [...menu.vinos.filter((v) => v.codigo || v.nombre || v.marca), ...data.vinos])
      }
    } catch {
      setError('No se pudieron traer los vinos elegidos.')
    }
  }

  // La ficha del evento cambió: se vuelve a armar desde ahí (queda en pantalla, sin guardar).
  const traerDelEvento = async () => {
    try {
      const { data } = await experienciasApi.menu(experiencia.id, true)
      setMenu(data.menu)
      setAviso('Traído de nuevo del evento — revisalo y guardá para que quede.')
    } catch {
      setError('No se pudo volver a traer del evento.')
    }
  }

  const revisar = async () => {
    setTrabajando('revisar')
    setError('')
    try {
      const { data } = await experienciasApi.revisarMenu(experiencia.id, menu)
      setCorrector(data.disponible)
      setHallazgos(data.hallazgos)
      return data.hallazgos
    } catch {
      setError('No se pudo revisar la ortografía.')
      return []
    } finally {
      setTrabajando('')
    }
  }

  // Aplica una corrección en su campo ("titulo", "vinos.3.nombre", "pasos.1.detalle").
  const corregir = (h, por) => {
    const [lista, i, sub] = h.campo.split('.')
    setMenu((m) => (sub
      ? { ...m, [lista]: m[lista].map((f, j) => (j === Number(i) ? { ...f, [sub]: reemplazar(f[sub], h.palabra, por) } : f)) }
      : { ...m, [lista]: reemplazar(m[lista], h.palabra, por) }))
    setHallazgos((hs) => hs.filter((x) => x !== h))
  }
  const ignorar = (h) => setHallazgos((hs) => hs.filter((x) => x !== h))
  const corregirTildes = () => hallazgos.filter((h) => h.tipo === 'tilde').forEach((h) => corregir(h, h.sugerencia))

  const guardar = async () => {
    setTrabajando('guardar')
    setError('')
    try {
      const { data } = await experienciasApi.guardarMenu(experiencia.id, menu)
      setMenu(data.menu)
      setUrlQr(data.url_qr)
      setGuardado(true)
      setAviso('Guardado.')
    } catch {
      setError('No se pudo guardar el menú.')
    } finally {
      setTrabajando('')
    }
  }

  const generar = async (sinRevisar = false) => {
    // Antes de imprimir pasa por el corrector: con faltas, se muestran y se
    // imprime recién con "Imprimir igual" (o después de corregir).
    if (!sinRevisar) {
      const hs = await revisar()
      if (hs.length) {
        setAntesDeImprimir(true)
        return
      }
    }
    setAntesDeImprimir(false)
    setTrabajando('pdf')
    setError('')
    try {
      const { data, headers } = await experienciasApi.pdfMenu(experiencia.id, menu, menu.formato)
      const nombre = /filename="?([^";]+)"?/.exec(headers['content-disposition'] || '')?.[1] || `menu-${menu.formato}.pdf`
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = nombre
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10000)
      setGuardado(true)
      setAviso(`PDF ${menu.formato} generado: ${nombre}`)
      // La URL del QR puede haber cambiado con los vinos: se relee lo guardado.
      experienciasApi.menu(experiencia.id).then(({ data: d }) => setUrlQr(d.url_qr)).catch(() => {})
    } catch (e) {
      let msg = 'No se pudo generar el PDF.'
      try { msg = JSON.parse(await e.response.data.text()).message || msg } catch { /* respuesta no JSON */ }
      setError(msg)
    } finally {
      setTrabajando('')
    }
  }

  return (
    <Modal title={`Menú impreso — ${experiencia.titulo}`} onClose={onClose} maxWidth="920px">
      {!menu ? (
        <p style={{ color: error ? colors.err : colors.textMeta }}>{error || 'Cargando…'}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>
          {!guardado && (
            <p style={estilos.nota}>
              Propuesta armada con los datos del evento. Revisala, vinculá los vinos a sus artículos para el QR y guardá.
            </p>
          )}

          {/* Formato */}
          <section style={estilos.seccion}>
            <h4 style={estilos.h4}>Formato</h4>
            <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap' }}>
              {Object.entries(formatos).map(([clave, label]) => {
                const activo = menu.formato === clave
                return (
                  <button
                    key={clave} type="button" onClick={() => set('formato', clave)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: space.md, padding: `${space.md}px ${space.lg}px`,
                      border: `1px solid ${activo ? colors.text : colors.border}`, borderRadius: radius.md,
                      background: activo ? colors.bgSoft : colors.bg, cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <Esquema vertical={clave === 'vertical'} />
                    <span style={{ fontSize: text.body, color: colors.text, fontWeight: activo ? 600 : 400 }}>{label}</span>
                  </button>
                )
              })}
            </div>
          </section>

          {/* Tapa */}
          <section style={estilos.seccion}>
            <h4 style={estilos.h4}>Tapa</h4>
            <div style={estilos.fila}>
              <Campo label="Arriba del título" ancho={140}>
                <input data-nocaps spellCheck lang="es" style={sx.input} value={menu.etiqueta} onChange={(e) => set('etiqueta', e.target.value)} />
              </Campo>
              <Campo label="Título (un renglón por línea)">
                <textarea data-nocaps spellCheck lang="es" rows={2} style={{ ...sx.input, resize: 'vertical' }} value={menu.titulo} onChange={(e) => set('titulo', e.target.value)} />
              </Campo>
            </div>
            <div style={estilos.fila}>
              <Campo label="Bajada">
                <input data-nocaps spellCheck lang="es" style={sx.input} value={menu.bajada} onChange={(e) => set('bajada', e.target.value)} />
              </Campo>
              <Campo label="Fecha" ancho={150}>
                <input data-nocaps spellCheck lang="es" style={sx.input} value={menu.fecha} onChange={(e) => set('fecha', e.target.value)} />
              </Campo>
              <Campo label="Pie" ancho={170}>
                <input data-nocaps spellCheck lang="es" style={sx.input} value={menu.pie} onChange={(e) => set('pie', e.target.value)} />
              </Campo>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: space.sm, fontSize: text.body, color: colors.text }}>
              <input type="checkbox" checked={menu.logo_casa_vg} onChange={(e) => set('logo_casa_vg', e.target.checked)} />
              Logo de Casa VG arriba
            </label>
          </section>

          {/* Vinos */}
          <section style={estilos.seccion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
              <h4 style={{ ...estilos.h4, margin: 0 }}>Los vinos</h4>
              <span style={{ fontSize: text.sec, color: vinculados ? colors.ok : colors.textMeta }}>
                {vinculados ? `El QR suma ${vinculados} ${vinculados === 1 ? 'vino' : 'vinos'} al carrito` : 'Sin vinos vinculados: el menú sale sin QR'}
              </span>
              {menu.vinos.some((v) => v.auto) && (
                <span style={{ fontSize: text.sec, color: colors.warn }}>
                  · {menu.vinos.filter((v) => v.auto).length} vinculados solos por nombre y añada: revisalos
                </span>
              )}
              {sinArticulo > 0 && vinculados > 0 && (
                <span style={{ fontSize: text.sec, color: colors.warn }}>
                  · {sinArticulo} sin artículo (se imprime, no va al QR)
                </span>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: space.sm }}>
                {urlQr && (
                  <a href={urlQr} target="_blank" rel="noreferrer" style={{ fontSize: text.sec, color: colors.link, alignSelf: 'center' }}>
                    Probar el QR ↗
                  </a>
                )}
                <Boton variante="secondary" sm onClick={() => setSelector('agregar')}>+ Agregar vinos</Boton>
              </div>
            </div>
            {menu.vinos.length === 0 && <p style={estilos.nota}>Todavía no hay vinos. Agregalos desde el maestro.</p>}
            {menu.vinos.map((v, i) => (
              <div key={i} style={estilos.renglon}>
                <span style={estilos.numero}>{String(i + 1).padStart(2, '0')}</span>
                <input data-nocaps spellCheck lang="es" style={{ ...sx.input, flex: '0 0 150px' }} placeholder="Marca" value={v.marca} onChange={(e) => setFila('vinos', i, 'marca', e.target.value)} />
                <input data-nocaps spellCheck lang="es" style={{ ...sx.input, flex: 1, minWidth: 0 }} placeholder="Nombre" value={v.nombre} onChange={(e) => setFila('vinos', i, 'nombre', e.target.value)} />
                <input data-nocaps spellCheck lang="es" style={{ ...sx.input, flex: '0 0 64px', ...sx.mono }} placeholder="Añada" value={v.anio} onChange={(e) => setFila('vinos', i, 'anio', e.target.value)} />
                {v.codigo ? (
                  <button
                    type="button" onClick={() => setSelector(i)}
                    title={v.auto ? 'Vinculado solo por nombre y añada: revisá que sea el artículo (click para cambiarlo). Al guardar queda firme.' : 'Cambiar el artículo'}
                    style={{ ...sx.badge, ...sx.mono, border: 'none', cursor: 'pointer', background: v.auto ? colors.warnBg : colors.okBg, color: v.auto ? colors.warn : colors.ok, flex: '0 0 auto' }}
                  >
                    QR · {v.codigo}{v.auto ? ' · revisá' : ''}
                  </button>
                ) : (
                  <Boton variante="ghost" sm onClick={() => setSelector(i)} title="Elegir el artículo: trae marca, nombre y añada, y lo suma al QR">
                    Vincular
                  </Boton>
                )}
                <Mover i={i} total={menu.vinos.length} onMover={mover('vinos')} onQuitar={quitar('vinos')} />
              </div>
            ))}
            <div style={estilos.fila}>
              <Campo label="Sommelier (pie de los vinos)">
                <input data-nocaps spellCheck lang="es" style={sx.input} value={menu.sommelier} onChange={(e) => set('sommelier', e.target.value)} />
              </Campo>
            </div>
          </section>

          {/* Menú */}
          <section style={estilos.seccion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.md }}>
              <h4 style={{ ...estilos.h4, margin: 0 }}>El menú</h4>
              <div style={{ marginLeft: 'auto' }}>
                <Boton variante="secondary" sm onClick={() => set('pasos', [...menu.pasos, { ...vacioPaso }])}>+ Paso</Boton>
              </div>
            </div>
            {menu.pasos.map((p, i) => (
              <div key={i} style={estilos.renglon}>
                <input data-nocaps spellCheck lang="es" style={{ ...sx.input, flex: '0 0 300px', minWidth: 0 }} placeholder="Plato" value={p.plato} onChange={(e) => setFila('pasos', i, 'plato', e.target.value)} />
                <input data-nocaps spellCheck lang="es" style={{ ...sx.input, flex: 1, minWidth: 0, fontStyle: 'italic' }} placeholder="Detalle (va en itálica)" value={p.detalle} onChange={(e) => setFila('pasos', i, 'detalle', e.target.value)} />
                <Mover i={i} total={menu.pasos.length} onMover={mover('pasos')} onQuitar={quitar('pasos')} />
              </div>
            ))}
            <div style={estilos.fila}>
              <Campo label="Chef (pie del menú)">
                <input data-nocaps spellCheck lang="es" style={sx.input} value={menu.chef} onChange={(e) => set('chef', e.target.value)} />
              </Campo>
            </div>
          </section>

          {/* Contratapa */}
          <section style={estilos.seccion}>
            <h4 style={estilos.h4}>Contratapa (junto al QR)</h4>
            <textarea data-nocaps spellCheck lang="es" rows={2} style={{ ...sx.input, resize: 'vertical' }} value={menu.texto_qr} onChange={(e) => set('texto_qr', e.target.value)} />
          </section>

          {!corrector && <p style={estilos.nota}>El corrector ortográfico no está disponible en este servidor.</p>}
          {hallazgos && hallazgos.length === 0 && corrector && (
            <p style={{ ...estilos.nota, color: colors.ok }}>Ortografía revisada: sin faltas.</p>
          )}
          {hallazgos && hallazgos.length > 0 && (
            <section style={estilos.revision}>
              <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: text.body, color: colors.warn }}>
                  {antesDeImprimir ? 'Antes de imprimir — ' : ''}
                  {hallazgos.length === 1 ? '1 posible falta' : `${hallazgos.length} posibles faltas`}
                </strong>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: space.sm }}>
                  {hallazgos.some((h) => h.tipo === 'tilde') && (
                    <Boton variante="secondary" sm onClick={corregirTildes}>Corregir todas las tildes</Boton>
                  )}
                  {antesDeImprimir && (
                    <Boton variante="ghost" sm onClick={() => generar(true)} disabled={!!trabajando}>Imprimir igual</Boton>
                  )}
                </div>
              </div>
              {hallazgos.map((h, k) => (
                <div key={`${h.campo}-${h.palabra}-${k}`} style={estilos.renglon}>
                  <span style={{ ...estilos.label, flex: '0 0 130px' }}>{nombreCampo(h.campo)}</span>
                  <span style={{ fontSize: text.body, color: colors.text, textDecoration: 'underline wavy', textDecorationColor: colors.errStrong }}>{h.palabra}</span>
                  <span style={{ color: colors.textMeta }}>→</span>
                  {h.sugerencias.map((sug) => (
                    <Boton key={sug} variante={h.tipo === 'tilde' ? 'secondary' : 'ghost'} sm onClick={() => corregir(h, sug)}>{sug}</Boton>
                  ))}
                  <Boton variante="ghost" sm onClick={() => ignorar(h)} title="Está bien así">Ignorar</Boton>
                </div>
              ))}
            </section>
          )}

          {error && <p style={estilos.error}>{error}</p>}

          <div style={{ display: 'flex', alignItems: 'center', gap: space.md, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {guardado && (
              <Boton variante="ghost" onClick={traerDelEvento} disabled={!!trabajando} title="Rearmar la propuesta con los datos actuales del evento (no guarda hasta que apretás Guardar)">
                ↺ Traer de nuevo del evento
              </Boton>
            )}
            {aviso && <span style={{ fontSize: text.sec, color: colors.ok, marginRight: 'auto' }}>{aviso}</span>}
            <Boton variante="ghost" onClick={revisar} disabled={!!trabajando}>
              {trabajando === 'revisar' ? 'Revisando…' : 'Revisar ortografía'}
            </Boton>
            <Boton variante="secondary" onClick={guardar} disabled={!!trabajando}>
              {trabajando === 'guardar' ? 'Guardando…' : 'Guardar'}
            </Boton>
            <Boton variante="primary" onClick={() => generar()} disabled={!!trabajando}>
              {trabajando === 'pdf' ? 'Generando…' : `↓ Generar PDF ${menu.formato}`}
            </Boton>
          </div>
        </div>
      )}

      {selector !== null && (
        <SelectorArticulosModal
          modo={selector === 'agregar' ? 'multiple' : 'simple'}
          titulo={selector === 'agregar' ? 'Agregar vinos al menú' : 'Vincular el vino a su artículo'}
          onConfirm={(filas) => alElegir(filas.map((f) => f.articulo))}
          onSelect={(articulo) => alElegir([articulo])}
          onClose={() => setSelector(null)}
        />
      )}
    </Modal>
  )
}

const estilos = {
  seccion: { display: 'flex', flexDirection: 'column', gap: space.md },
  h4: { margin: 0, fontSize: text.sec, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.04em' },
  label: { fontSize: text.sec, color: colors.textSec },
  fila: { display: 'flex', gap: space.md, flexWrap: 'wrap' },
  renglon: { display: 'flex', alignItems: 'center', gap: space.sm },
  numero: { ...sx.mono, flex: '0 0 22px', fontSize: text.sec, color: colors.textMeta },
  nota: { margin: 0, fontSize: text.sec, color: colors.textSec },
  revision: { display: 'flex', flexDirection: 'column', gap: space.sm, padding: space.lg, background: colors.warnSoft, border: `1px solid ${colors.warnBorder}`, borderRadius: radius.md },
  error: { margin: 0, background: colors.errBg, color: colors.err, padding: space.md, borderRadius: radius.sm, fontSize: text.body },
}

